/**
 * What kind of video this is, and how that changes the writing.
 *
 * Tone says how it should sound and audience says who it is for; neither says
 * what the video is *doing*. A walkthrough and a release note about the same
 * recording want different scripts — one names every screen in the order you
 * meet it, the other leads with what changed and skips the parts that did not.
 *
 * Each entry carries real direction rather than a label. "Purpose: Product
 * walkthrough" in a prompt is a category, and a model reading it has to guess
 * what the category implies; the guidance is what actually moves the writing.
 */
export interface VideoPurpose {
  id: string;
  label: string;
  /** One line under the chip, so the choice can be made without guessing. */
  hint: string;
  /** Appended to the writing instructions. Written as direction to a writer. */
  guidance: string;
}

export const videoPurposes: readonly VideoPurpose[] = [
  {
    id: "walkthrough",
    label: "Product walkthrough",
    hint: "Show the product end to end, in the order someone would use it",
    guidance:
      "This is a walkthrough. Take the viewer through the product in the order they would meet it. " +
      "Name each screen as you arrive, and say what an action achieves rather than that a click happened — " +
      '"name the project and it opens in the editor", not "click the name field". ' +
      "Assume they have never seen this product before, so do not use a term the video has not shown yet.",
  },
  {
    id: "feature",
    label: "New feature",
    hint: "Announce something that changed, and show it",
    guidance:
      "This announces something new. Lead with what changed and why it matters, then show it. " +
      "Do not re-explain parts of the product that have not changed; the viewer already uses this product.",
  },
  {
    id: "onboarding",
    label: "Getting started",
    hint: "Something the viewer follows along with, step by step",
    guidance:
      "This is a set of instructions someone will follow along with while it plays. Address them directly, " +
      "one action per scene, and leave them a beat to catch up before the next one. Never describe two steps in one line.",
  },
  {
    id: "sales",
    label: "Sales demo",
    hint: "Tie each step to the problem it solves",
    guidance:
      "This is shown to someone deciding whether to buy. Tie each step to the problem it solves for them rather " +
      "than to the button that does it. Keep moving; skip configuration detail nobody asks about before they have said yes.",
  },
  {
    id: "support",
    label: "Support answer",
    hint: "Answer one question, and stop",
    guidance:
      "This answers one question. Say what to do, in order, and stop when it is answered. No introduction, " +
      "no summary, nothing about the product beyond what the question needs.",
  },
  {
    id: "handover",
    label: "Internal handover",
    hint: "For people who already work here",
    guidance:
      "The viewer works here. Use the team's own vocabulary, name systems and screens directly, and do not " +
      "explain anything a colleague would already know. Say why a step is done this way where it is not obvious.",
  },
] as const;

/** The writing direction for a purpose, or nothing when it is unset or unknown. */
export function purposeGuidance(id: string | null | undefined): string | null {
  if (!id) return null;
  return videoPurposes.find((purpose) => purpose.id === id)?.guidance ?? null;
}

/**
 * Tone and audience, in one place.
 *
 * These lived as local constants on the upload screen, which is why they could
 * only be chosen once: the editor had no list to offer. They sit beside the
 * purposes because all three answer the same question — what the script should
 * sound like, who it is for, and what it is doing.
 *
 * Written as instructions to a writer rather than as UI labels, because that is
 * what they become: the prompt carries them through verbatim.
 */
export const tonePresets = [
  "Clear and friendly",
  "Confident and direct",
  "Warm and conversational",
  "Technical and precise",
  "Upbeat and energetic",
  "Calm and measured",
] as const;

export const audiencePresets = [
  "New customers",
  "Existing users",
  "Developers",
  "Executives",
  "Internal team",
  "Prospects on a sales call",
] as const;
